import { ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { MenuService, MenuSection } from '../../services/menu.service';

export interface DockState {
  showWindow: boolean;
  windowTitle: string;
  windowItems: { name: string; desc: string; gradient: string; svgIcon: string; route: string; }[];
}

export class TopbarDockCollab {

  private dockEl: HTMLElement | null = null;
  private dockItems: HTMLElement[] = [];
  private readonly DOCK_BASE = 60;
  private readonly DOCK_MAX = 88;
  private readonly DOCK_RANGE = 140;
  private boundDockMove = this.onDockMouseMove.bind(this);
  private boundDockLeave = this.onDockMouseLeave.bind(this);

  private _svgCache = new Map<string, SafeHtml>();
  private dockDataMap: Record<string, { name: string; desc: string; gradient: string; svgIcon: string; route: string; }[]> = {};

  constructor(
    private elementRef: ElementRef,
    private sanitizer: DomSanitizer,
    private router: Router,
    private translate: TranslateService,
    private menuService: MenuService,
  ) {}

  svgFor(svgInner?: string, size = 15): SafeHtml {
    if (!svgInner) return '';
    const key = `${svgInner}__${size}`;
    let cached = this._svgCache.get(key);
    if (!cached) {
      cached = this.sanitizer.bypassSecurityTrustHtml(
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ` +
        `style="width:${size}px;height:${size}px;color:white;position:relative;z-index:1;flex-shrink:0">${svgInner}</svg>`
      );
      this._svgCache.set(key, cached);
    }
    return cached;
  }

  setupDockMagnification(): void {
    setTimeout(() => {
      this.dockEl = this.elementRef.nativeElement.querySelector('.dock');
      if (this.dockEl) {
        this.dockItems = Array.from(this.dockEl.querySelectorAll('.di'));
        this.dockEl.addEventListener('mousemove', this.boundDockMove);
        this.dockEl.addEventListener('mouseleave', this.boundDockLeave);
      }
    }, 100);
  }

  destroyDockListeners(): void {
    if (this.dockEl) {
      this.dockEl.removeEventListener('mousemove', this.boundDockMove);
      this.dockEl.removeEventListener('mouseleave', this.boundDockLeave);
    }
  }

  private onDockMouseMove(e: MouseEvent): void {
    const mx = e.clientX;
    this.dockItems.forEach(d => {
      const icon = d.querySelector('.di-icon') as HTMLElement;
      const svg = icon?.querySelector('span') as HTMLElement;
      if (!icon) return;
      const r = d.getBoundingClientRect();
      const c = r.left + r.width / 2;
      const dist = Math.abs(mx - c);
      const sc = Math.max(0, 1 - dist / this.DOCK_RANGE);
      const ease = Math.cos((1 - sc) * Math.PI / 2);
      const sz = this.DOCK_BASE + (this.DOCK_MAX - this.DOCK_BASE) * ease;
      const isz = Math.round(sz * 0.5);
      const rd = 12 + (sz - this.DOCK_BASE) * 0.06;
      icon.style.width = sz + 'px';
      icon.style.height = sz + 'px';
      icon.style.borderRadius = rd + 'px';
      if (svg) {
        svg.style.width = isz + 'px';
        svg.style.height = isz + 'px';
      }
    });
  }

  private onDockMouseLeave(): void {
    this.dockItems.forEach(d => {
      const icon = d.querySelector('.di-icon') as HTMLElement;
      const svg = icon?.querySelector('span') as HTMLElement;
      if (!icon) return;
      icon.style.width = this.DOCK_BASE + 'px';
      icon.style.height = this.DOCK_BASE + 'px';
      icon.style.borderRadius = '15px';
      if (svg) {
        svg.style.width = '28px';
        svg.style.height = '28px';
      }
    });
  }

  buildDockDataMap(): void {
    this.dockDataMap['dashboard'] = [
      { name: 'TOPBAR.OVERVIEW', desc: 'TOPBAR.KPIS', gradient: 'g-indigo', svgIcon: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>', route: '/dashboard' },
      { name: 'TOPBAR.ACTIVITY', desc: 'TOPBAR.OPERATIONS', gradient: 'g-violet', svgIcon: '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>', route: '/dashboard' },
      { name: 'TOPBAR.ALERTS', desc: '14', gradient: 'g-rose', svgIcon: '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>', route: '/notifications' }
    ];
  }

  getDockWindowItems(section: MenuSection): { name: string; desc: string; gradient: string; svgIcon: string; route: string; }[] {
    return section.items
      .filter(i => !i.separator)
      .map(i => ({
        name: i.label,
        desc: i.description || '',
        gradient: i.gradient || 'g-indigo',
        svgIcon: i.svgIcon || '',
        route: i.routerLink || '/'
      }));
  }

  openDockWindow(labelKey: string, key: string, state: DockState): void {
    const title = this.translate.instant(labelKey) || labelKey;
    if (key === 'dashboard') {
      state.windowTitle = title;
      state.windowItems = this.dockDataMap['dashboard'];
      state.showWindow = true;
      return;
    }
    const sections = this.menuService.menuSections();
    const section = sections.find(s => s.label === key);
    if (section) {
      state.windowTitle = title;
      state.windowItems = this.getDockWindowItems(section);
      state.showWindow = true;
    }
  }

  closeWindow(state: DockState): void {
    state.showWindow = false;
  }

  windowItemClick(route: string, state: DockState): void {
    state.showWindow = false;
    this.router.navigate([route]);
  }
}
